import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import {
  getDefaultUserFilters,
  UserFilter,
  UserOrderField,
} from '../../interfaces/User/UserFilter';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { UserSummary } from '../../interfaces/User/UserSummary';
import { getUsers } from '../../api/User';
import { Pagination } from '../../interfaces/Pagination';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { UserSearchBox } from './UserSearchBox';
import { UsersTable } from './UsersTable';
import { SortingOrder } from '../../interfaces/SortingOrder';

export const UsersPage = () => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState(getDefaultUserFilters());
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<PaginatedResponse<UserSummary>>({
    data: [],
    totalCount: 0,
  });

  const refreshUsers = (filters?: UserFilter) => {
    setLoading(true);
    getUsers(filters ?? filter)
      .then((res) => setUsers(res))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshUsers();
  }, []);

  const handleSearch = () => {
    const newFilter: UserFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshUsers(newFilter);
  };

  const handleChangeOrder = (
    newOrderBy: UserOrderField,
    newOrder: SortingOrder,
  ) => {
    const newFilter = {
      ...filter,
      orderBy: newOrderBy,
      order: newOrder,
    };

    setFilter(newFilter);
    refreshUsers(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter: UserFilter = {
      ...filter,
      pagination: p,
    };

    setFilter(newFilter);
    refreshUsers(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle }}>
          Kullanıcılar
        </Typography>

        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/users/new')}
          sx={{ ...sharedStyles.buttonText, mb: 1.5 }}
        >
          Yeni Kullanıcı
        </Button>
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <UserSearchBox
            label='Kullanıcı ID'
            filters={filter}
            onChange={setFilter}
            fieldName='id'
            onEnter={handleSearch}
          />

          <UserSearchBox
            label='İsim'
            filters={filter}
            onChange={setFilter}
            fieldName='name'
            onEnter={handleSearch}
          />

          <UserSearchBox
            label='Email'
            filters={filter}
            onChange={setFilter}
            fieldName='email'
            onEnter={handleSearch}
          />

          <UserSearchBox
            label='Telefon'
            filters={filter}
            onChange={setFilter}
            fieldName='phone'
            onEnter={handleSearch}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() =>
                setFilter({
                  pagination: filter.pagination,
                  order: filter.order,
                  orderBy: filter.orderBy,
                })
              }
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Kullanıcı Ara
            </Button>
          </Box>
        </Box>
      </Paper>

      <UsersTable
        users={users.data}
        totalCount={users.totalCount}
        orderBy={filter.orderBy}
        order={filter.order}
        onChangeOrder={handleChangeOrder}
        pagination={filter.pagination}
        onPagination={handlePagination}
      />
    </Box>
  );
};
